import { storeToRefs } from "pinia";
import type { AuthTokens } from "@/shared/auth/@types/AuthTokens";
import { useAppStateStore } from "@/shared/stores/AppStateStore";
import {
  clearAuthTokensCookie,
  getAuthTokensFromCookie,
  setAuthTokensCookie,
} from "@/shared/auth/helper/authTokensCookie.helper";
import { REQUEST_TOKEN_URI } from "@/shared/auth/helper/authConstants.helper";

interface TokenRefreshResponse {
  id_token: string;
  access_token: string;
  refresh_token?: string;
}

export const refreshTokens = async (
  clientId: string,
): Promise<AuthTokens | undefined> => {
  const appStateStore = useAppStateStore();
  const { waitForRouteChange } = storeToRefs(appStateStore);
  const { showLoadingOverlay, hideLoadingOverlay } = appStateStore;

  showLoadingOverlay();

  try {
    const tokens = getAuthTokensFromCookie();
    if (!tokens?.refreshToken) {
      throw new Error("No refresh token available");
    }

    const response = await new Promise<TokenRefreshResponse>(
      (resolve, reject) => {
        const client = new XMLHttpRequest();
        client.open("POST", REQUEST_TOKEN_URI, true);

        client.setRequestHeader(
          "Content-Type",
          "application/x-www-form-urlencoded",
        );

        const requestBody = `grant_type=refresh_token&client_id=${clientId}&refresh_token=${tokens.refreshToken}`;

        client.onloadend = () => {
          if (client.status === 200) {
            try {
              const parsedResponse = JSON.parse(client.response);
              resolve(parsedResponse);
            } catch {
              reject(new Error("Invalid token refresh response"));
            }
          } else {
            reject(new Error("Token refresh failed"));
          }
        };

        client.onerror = () => {
          reject(new Error("Network error during token refresh"));
        };

        client.send(requestBody);
      },
    );

    if (response.id_token && response.access_token) {
      setAuthTokensCookie({
        idToken: response.id_token,
        accessToken: response.access_token,
        refreshToken: response.refresh_token ?? tokens.refreshToken, // The refresh token is only returned by the response, if "token rotation" is enabled in salesforce.
      });
    }

    if (!waitForRouteChange.value) hideLoadingOverlay();

    return {
      idToken: response.id_token,
      accessToken: response.access_token,
      refreshToken: response.refresh_token ?? tokens.refreshToken,
    };
  } catch {
    hideLoadingOverlay();
    clearAuthTokensCookie();
    window.location.href = "/login";
  }
};
